import { Controller } from "@hotwired/stimulus"

const CELL_SIZE = 13
const CELL_GAP = 3
const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""]
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

function _cssVar(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
}

function isDark() {
  return document.documentElement.classList.contains("dark")
}

function getColors() {
  if (isDark()) {
    return [
      "rgba(255,255,255,0.04)",
      "#3b2d6b",
      "#6a40c9",
      _cssVar("--color-vivid-purple"),
      "#c4a8ff",
    ]
  }
  return [
    "rgba(0,0,0,0.04)",
    "#e8deff",
    "#c4a0ff",
    _cssVar("--color-vivid-purple"),
    "#2A006B",
  ]
}

function colorForCount(count, maxCount) {
  const colors = getColors()
  if (count === 0) return colors[0]
  if (maxCount === 0) return colors[0]
  const ratio = count / maxCount
  if (ratio <= 0.25) return colors[1]
  if (ratio <= 0.5) return colors[2]
  if (ratio <= 0.75) return colors[3]
  return colors[4]
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00")
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = { data: Array }

  connect() {
    this.render()
    this._onAppearanceChange = () => this.render()
    window.addEventListener("appearance:change", this._onAppearanceChange)
  }

  disconnect() {
    this.removeTooltip()
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  render() {
    const container = this.canvasTarget
    container.innerHTML = ""

    const data = this.dataValue
    if (!data || data.length === 0) return

    const countMap = new Map()
    let maxCount = 0
    data.forEach((d) => {
      countMap.set(d.date, d.count)
      if (d.count > maxCount) maxCount = d.count
    })

    const leftPad = 32
    const topPad = 18
    const ns = "http://www.w3.org/2000/svg"

    // Build week columns from data
    const firstDate = new Date(data[0].date + "T00:00:00")
    const lastDate = new Date(data[data.length - 1].date + "T00:00:00")

    // Adjust to start on a Sunday
    const startDay = firstDate.getDay()
    const startDate = new Date(firstDate)
    startDate.setDate(startDate.getDate() - startDay)

    const weeks = []
    const current = new Date(startDate)
    while (current <= lastDate) {
      const week = []
      for (let d = 0; d < 7; d++) {
        const dateStr = current.toISOString().slice(0, 10)
        week.push({
          date: dateStr,
          count: countMap.get(dateStr) || 0,
          inRange: current >= firstDate && current <= lastDate,
        })
        current.setDate(current.getDate() + 1)
      }
      weeks.push(week)
    }

    const svgWidth = leftPad + weeks.length * (CELL_SIZE + CELL_GAP) + 10
    const svgHeight = topPad + 7 * (CELL_SIZE + CELL_GAP) + 30

    const svg = document.createElementNS(ns, "svg")
    svg.setAttribute("width", "100%")
    svg.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`)
    svg.style.display = "block"

    // Day labels
    DAY_LABELS.forEach((label, i) => {
      if (!label) return
      const text = document.createElementNS(ns, "text")
      text.setAttribute("x", leftPad - 6)
      text.setAttribute(
        "y",
        topPad + i * (CELL_SIZE + CELL_GAP) + CELL_SIZE - 2,
      )
      text.setAttribute("text-anchor", "end")
      text.setAttribute("fill", _cssVar("--app-text-faint"))
      text.setAttribute("font-size", "10")
      text.textContent = label
      svg.appendChild(text)
    })

    // Month labels
    let lastMonth = -1
    weeks.forEach((week, wi) => {
      const firstInRange = week.find((d) => d.inRange)
      if (!firstInRange) return
      const month = new Date(firstInRange.date + "T00:00:00").getMonth()
      if (month !== lastMonth) {
        lastMonth = month
        const text = document.createElementNS(ns, "text")
        text.setAttribute("x", leftPad + wi * (CELL_SIZE + CELL_GAP))
        text.setAttribute("y", topPad - 6)
        text.setAttribute("fill", _cssVar("--app-text-faint"))
        text.setAttribute("font-size", "10")
        text.textContent = MONTH_NAMES[month]
        svg.appendChild(text)
      }
    })

    // Cells
    weeks.forEach((week, wi) => {
      week.forEach((day, di) => {
        const rect = document.createElementNS(ns, "rect")
        const x = leftPad + wi * (CELL_SIZE + CELL_GAP)
        const y = topPad + di * (CELL_SIZE + CELL_GAP)
        rect.setAttribute("x", x)
        rect.setAttribute("y", y)
        rect.setAttribute("width", CELL_SIZE)
        rect.setAttribute("height", CELL_SIZE)
        rect.setAttribute("rx", 2)
        rect.setAttribute(
          "fill",
          day.inRange ? colorForCount(day.count, maxCount) : "transparent",
        )
        rect.style.cursor = day.inRange ? "pointer" : "default"

        if (day.inRange) {
          rect.addEventListener("mouseenter", (event) => {
            this.showTooltip(event, day)
          })
          rect.addEventListener("mousemove", (event) => {
            this.moveTooltip(event)
          })
          rect.addEventListener("mouseleave", () => {
            this.removeTooltip()
          })
        }

        svg.appendChild(rect)
      })
    })

    // Legend
    const legendX = svgWidth - 130
    const legendY = svgHeight - 14

    const lessText = document.createElementNS(ns, "text")
    lessText.setAttribute("x", legendX)
    lessText.setAttribute("y", legendY + 9)
    lessText.setAttribute("fill", _cssVar("--app-text-faint"))
    lessText.setAttribute("font-size", "10")
    lessText.textContent = "Less"
    svg.appendChild(lessText)

    getColors().forEach((color, i) => {
      const rect = document.createElementNS(ns, "rect")
      rect.setAttribute("x", legendX + 28 + i * (CELL_SIZE + 2))
      rect.setAttribute("y", legendY)
      rect.setAttribute("width", CELL_SIZE)
      rect.setAttribute("height", CELL_SIZE)
      rect.setAttribute("rx", 2)
      rect.setAttribute("fill", color)
      svg.appendChild(rect)
    })

    const moreText = document.createElementNS(ns, "text")
    moreText.setAttribute(
      "x",
      legendX + 28 + getColors().length * (CELL_SIZE + 2) + 4,
    )
    moreText.setAttribute("y", legendY + 9)
    moreText.setAttribute("fill", _cssVar("--app-text-faint"))
    moreText.setAttribute("font-size", "10")
    moreText.textContent = "More"
    svg.appendChild(moreText)

    container.appendChild(svg)
  }

  showTooltip(event, day) {
    this.removeTooltip()

    const tip = document.createElement("div")
    const tooltipBg = _cssVar("--app-text")
    const tooltipFg = _cssVar("--app-surface")
    tip.style.cssText = `position:fixed;pointer-events:none;background:${tooltipBg};color:${tooltipFg};padding:6px 10px;border-radius:6px;font-size:12px;line-height:1.4;z-index:50;white-space:nowrap;`

    const count = document.createElement("span")
    count.style.fontWeight = "600"
    count.textContent =
      day.count === 0
        ? "No laps"
        : `${day.count} lap${day.count === 1 ? "" : "s"}`
    tip.appendChild(count)

    tip.appendChild(document.createElement("br"))

    const date = document.createElement("span")
    date.style.opacity = "0.7"
    date.textContent = formatDate(day.date)
    tip.appendChild(date)

    document.body.appendChild(tip)
    this._tooltip = tip
    this.moveTooltip(event)
  }

  moveTooltip(event) {
    if (!this._tooltip) return
    this._tooltip.style.left = `${event.clientX + 12}px`
    this._tooltip.style.top = `${event.clientY - 10}px`
  }

  removeTooltip() {
    if (this._tooltip) {
      this._tooltip.remove()
      this._tooltip = null
    }
  }
}
